/*
 * <media-amazon-modal>
 * Modal de medios estilo Amazon: pestañas Videos/Images, stage + sidebar.
 * Se abre con window.openProductMediaModal('videos'|'images', key).
 * Reutiliza helpers globales del theme (trapFocus / removeTrapFocus / pauseAllMedia) si existen.
 *
 * IMÁGENES POR VARIANTE:
 * Al abrir la pestaña Images, la grilla muestra SOLO las fotos de la variante
 * seleccionada (mismo matching por alt-text que media-gallery.js) y agrega una
 * celda extra "+N Photos" al final. Al pulsarla se revelan TODAS las fotos,
 * agrupadas por variante con un encabezado por color.
 */
if (!customElements.get('media-amazon-modal')) {
  customElements.define('media-amazon-modal', class MediaAmazonModal extends HTMLElement {
    constructor() {
      super();
      this.currentTab = 'images';
      this.activeImageIndex = 0;
      this.openedBy = null;
      this.expanded = false;       // true cuando el usuario pulsó "+N Photos"
      this.navList = [];           // imágenes navegables (visibles, o todas si expanded)
      this.activeImageItem = null; // referencia a la miniatura activa
      this.moreTile = null;        // celda "+N Photos" (si existe)
    }

    connectedCallback() {
      this.dialog = this.querySelector('.mam-dialog');
      this.stage = this.querySelector('.mam-stage__media');
      this.stageTitle = this.querySelector('.mam-stage__title');
      this.imagesHeading = this.querySelector('[data-images-heading]');
      this.grid = this.querySelector('.mam-grid');
      this.tabs = Array.from(this.querySelectorAll('.mam-tab'));
      this.imageItems = Array.from(this.querySelectorAll('.mam-image-item'));
      this.videoItems = Array.from(this.querySelectorAll('.mam-video-item'));

      this.navList = this.imageItems.slice();
      this.bindEvents();

      // Registrar el opener global (lo usa media-gallery.js)
      window.openProductMediaModal = (tab, key) => this.open(tab, key);
    }

    bindEvents() {
      this.tabs.forEach((t) => t.addEventListener('click', () => {
        if (t.dataset.tab === 'videos') {
          this.selectVideo(this.activeVideoIndex || 0);
        } else {
          this.selectImageItem(this.activeImageItem || this.navList[0] || this.imageItems[0]);
          this.setTab('images');
        }
      }));

      this.imageItems.forEach((it) => it.addEventListener('click', () => this.selectImageItem(it)));
      this.videoItems.forEach((it, i) => it.addEventListener('click', () => this.selectVideo(i)));

      const prev = this.querySelector('.mam-nav--prev');
      const next = this.querySelector('.mam-nav--next');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));

      const closeBtn = this.querySelector('.mam-close');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());

      // Cerrar al hacer click fuera del diálogo (en el overlay = el propio host)
      this.addEventListener('click', (e) => { if (e.target === this) this.close(); });

      // Teclado: Escape cierra; flechas navegan imágenes
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { this.close(); return; }
        if (this.currentTab !== 'images' || this.navList.length < 2) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
      });

      // Si cambia la variante, volvemos a filtrar (y reseteamos el "expandido").
      const onVariantChange = () => {
        this.expanded = false;
        if (this.hasAttribute('open')) {
          this.applyVariantFilter();
          this.selectImageItem(this.navList[0] || this.imageItems[0]);
        }
      };
      document.addEventListener('variant:change', onVariantChange);
      document.addEventListener('variant:changed', onVariantChange);
      document.addEventListener('product:variant-change', onVariantChange);

      // Zoom tipo lupa en la imagen grande (solo pestaña IMAGES).
      // Mueve el foco del zoom a las coordenadas dadas (en píxeles de viewport).
      const ZOOM = 2.4;
      const zoomTo = (clientX, clientY) => {
        const img = this.stage.querySelector('.mam-stage__img');
        if (!img) return;
        const r = this.stage.getBoundingClientRect();
        const x = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
        const y = Math.max(0, Math.min(100, ((clientY - r.top) / r.height) * 100));
        img.style.transformOrigin = `${x}% ${y}%`;
        img.style.transform = `scale(${ZOOM})`;
      };

      // --- Desktop: lupa al pasar el mouse ---
      this.stage.addEventListener('mouseenter', () => {
        if (this.currentTab === 'images' && this.stage.querySelector('.mam-stage__img')) {
          this.stage.classList.add('is-zooming');
        }
      });
      this.stage.addEventListener('mousemove', (e) => {
        if (this.currentTab !== 'images') return;
        zoomTo(e.clientX, e.clientY);
      });
      this.stage.addEventListener('mouseleave', () => this.resetZoom());

      // --- Mobile: mantén el dedo presionado y arrástralo para hacer zoom ---
      // Mientras el dedo esté sobre la imagen hay zoom; al soltar, vuelve a normal.
      this.stage.addEventListener('touchstart', (e) => {
        if (this.currentTab !== 'images') return;
        if (e.touches.length !== 1 || !this.stage.querySelector('.mam-stage__img')) return;
        // Evita el menú nativo de iOS ("Guardar imagen / Copiar") al mantener
        // el dedo presionado, que estorbaba el zoom.
        if (e.cancelable) e.preventDefault();
        this.stage.classList.add('is-zooming');
        zoomTo(e.touches[0].clientX, e.touches[0].clientY);
      }, { passive: false });
      this.stage.addEventListener('touchmove', (e) => {
        if (this.currentTab !== 'images') return;
        if (e.touches.length !== 1 || !this.stage.classList.contains('is-zooming')) return;
        e.preventDefault(); // evita el scroll de la página mientras se hace zoom
        zoomTo(e.touches[0].clientX, e.touches[0].clientY);
      }, { passive: false });
      this.stage.addEventListener('touchend', () => this.resetZoom());
      this.stage.addEventListener('touchcancel', () => this.resetZoom());
    }

    resetZoom() {
      if (!this.stage) return;
      this.stage.classList.remove('is-zooming');
      const img = this.stage.querySelector('.mam-stage__img');
      if (img) { img.style.transform = 'scale(1)'; img.style.transformOrigin = 'center center'; }
    }

    // ===== Selección actual (mismas reglas que media-gallery.js) =====

    norm(v) { return (v || '').toLowerCase().trim().replace(/\s+/g, '-'); }

    productInfoEl() {
      return document.querySelector('product-info') || document.querySelector('.product-form');
    }

    selectionContext() {
      const productInfo = this.productInfoEl();
      if (!productInfo) return null;
      const checked = Array.from(productInfo.querySelectorAll('input[type="radio"]:checked, select'));
      const activeTokens = checked.map((i) => this.norm(i.value)).filter(Boolean);
      const allPossibleValues = Array.from(productInfo.querySelectorAll('input[type="radio"], select option'))
        .map((el) => this.norm(el.value || el.textContent))
        .filter((t) => t && t !== 'all');
      const idInput = productInfo.querySelector('[name="id"]');
      const currentVariantId = idInput ? String(idInput.value) : '';
      return { activeTokens, allPossibleValues, currentVariantId };
    }

    productUsesFiltering(ctx) {
      return this.imageItems.some((item) => {
        const tags = (item.dataset.color || '').toLowerCase().split(',').map((t) => t.trim());
        return tags.some((tag) => ctx.allPossibleValues.includes(tag));
      });
    }

    matchesVariant(item, ctx) {
      const tags = (item.dataset.color || '').toLowerCase().split(',').map((t) => t.trim()).filter(Boolean);
      const variantIds = (item.dataset.variants || '').split(',').map((v) => v.trim()).filter(Boolean);
      const isAll = tags.some((t) => t === 'all' || t === 'all-show');
      const isAssignedToOther = variantIds.length > 0 && !variantIds.includes(ctx.currentVariantId);
      const matchesAlt = ctx.activeTokens.some((t) => tags.includes(t));
      const hasMismatch = tags.some((t) => ctx.allPossibleValues.includes(t) && !ctx.activeTokens.includes(t));
      return isAll || (!isAssignedToOther && matchesAlt && !hasMismatch);
    }

    // Orden y etiquetas de los grupos por variante, leídos del formulario.
    variantOrder() {
      const productInfo = this.productInfoEl();
      const order = [];
      const labelMap = {};
      if (productInfo) {
        Array.from(productInfo.querySelectorAll('input[type="radio"]')).forEach((r) => {
          const t = this.norm(r.value);
          if (t && !(t in labelMap)) { labelMap[t] = (r.value || '').trim(); order.push(t); }
        });
      }
      return { order, labelMap };
    }

    // ===== Construcción de la grilla =====

    removeMoreTile() {
      if (this.grid) this.grid.querySelectorAll('.mam-more-tile').forEach((t) => t.remove());
      this.moreTile = null;
    }

    removeGroupHeaders() {
      if (this.grid) this.grid.querySelectorAll('.mam-group-header').forEach((h) => h.remove());
    }

    updateHeading(n) {
      if (this.imagesHeading) this.imagesHeading.textContent = `${n} image${n !== 1 ? 's' : ''}`;
    }

    // Inserta la celda extra "+N Photos" al final de la grilla.
    addMoreTile(count, sampleHidden) {
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'mam-more-tile';
      const bgImg = sampleHidden && sampleHidden.querySelector('img');
      const bgSrc = bgImg ? bgImg.getAttribute('src') : '';
      tile.innerHTML =
        `<img src="${bgSrc}" alt="" aria-hidden="true">` +
        `<span class="mam-more-label"><span class="mam-more-num">+${count}</span>` +
        `<span class="mam-more-word">Photos</span></span>`;
      tile.setAttribute('aria-label', `Ver ${count} fotos más`);
      tile.addEventListener('click', () => this.expand());
      this.grid.appendChild(tile);
      this.moreTile = tile;
    }

    // Muestra todas las fotos en su orden original (sin agrupar).
    showAll() {
      this.removeMoreTile();
      this.removeGroupHeaders();
      this.imageItems.forEach((it) => { it.style.display = ''; });
      this.navList = this.imageItems.slice();
      this.updateHeading(this.navList.length);
    }

    // Filtra la grilla a la variante activa y agrega "+N Photos" como celda extra.
    applyVariantFilter() {
      this.removeMoreTile();
      this.removeGroupHeaders();
      const ctx = this.selectionContext();
      if (!ctx || this.expanded || !this.productUsesFiltering(ctx)) {
        this.showAll();
        return;
      }
      const visible = [];
      const hidden = [];
      this.imageItems.forEach((it) => {
        if (this.matchesVariant(it, ctx)) { it.style.display = ''; visible.push(it); }
        else { it.style.display = 'none'; hidden.push(it); }
      });
      // Nunca dejamos la grilla vacía: si el filtro no acertó, mostramos todo.
      if (visible.length === 0) {
        this.showAll();
        return;
      }
      this.navList = visible;
      this.updateHeading(visible.length);
      if (hidden.length > 0) {
        this.addMoreTile(hidden.length, hidden[0]);
      }
    }

    // Revela TODAS las fotos, agrupadas por variante con encabezados.
    expand() {
      this.expanded = true;
      this.removeMoreTile();
      this.removeGroupHeaders();

      const { order, labelMap } = this.variantOrder();
      const GENERIC = '__generic__';
      const groups = new Map();
      this.imageItems.forEach((it) => {
        it.style.display = '';
        const tags = (it.dataset.color || '').toLowerCase().split(',').map((t) => t.trim()).filter(Boolean);
        let key = order.find((t) => tags.includes(t));
        if (!key) key = GENERIC;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(it);
      });

      const orderedKeys = order.filter((k) => groups.has(k));
      if (groups.has(GENERIC)) orderedKeys.push(GENERIC);

      const frag = document.createDocumentFragment();
      const navList = [];
      orderedKeys.forEach((key) => {
        const header = document.createElement('div');
        header.className = 'mam-group-header';
        header.textContent = key === GENERIC ? 'General' : (labelMap[key] || key);
        frag.appendChild(header);
        groups.get(key).forEach((it) => { frag.appendChild(it); navList.push(it); });
      });

      this.grid.innerHTML = '';
      this.grid.appendChild(frag);
      this.navList = navList;
      this.updateHeading(this.imageItems.length);
    }

    open(tab, key) {
      // Preparamos SIEMPRE la grilla de imágenes (filtrada y ordenada por
      // variante), aunque el modal se abra en la pestaña de videos. Así, al
      // cambiar luego a IMAGES, las fotos ya están en su orden correcto y no
      // "se pierde" el orden.
      this.expanded = false;
      this.applyVariantFilter();

      const wantVideos = tab === 'videos' && this.videoItems.length > 0;

      if (wantVideos) {
        let vidx = 0;
        if (key != null && key !== '' && !isNaN(parseInt(key, 10))) {
          const n = parseInt(key, 10);
          if (n >= 0 && n < this.videoItems.length) vidx = n;
        }
        this.selectVideo(vidx);
      } else {
        let item = null;
        if (key != null && key !== '') {
          item = this.imageItems.find((it) => String(it.dataset.mediaId) === String(key));
        }
        // Si la foto pedida es de otra variante (oculta), expandimos para mostrarla.
        if (item && item.style.display === 'none') {
          this.expand();
        }
        if (!item) item = this.navList[0] || this.imageItems[0];
        this.selectImageItem(item);
      }

      this.openedBy = document.activeElement;
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      if (typeof window.trapFocus === 'function') {
        window.trapFocus(this, this.dialog);
      } else if (this.dialog) {
        this.dialog.focus();
      }
    }

    close() {
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      if (this.stage) this.stage.innerHTML = '';
      this.expanded = false; // la próxima apertura vuelve a filtrar por variante
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(this.openedBy);
      else if (this.openedBy && this.openedBy.focus) this.openedBy.focus();
    }

    setTab(tab) {
      this.currentTab = tab;
      this.setAttribute('data-active-tab', tab);
      this.tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.tab === tab));
    }

    selectImageItem(item) {
      if (!item) return;
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      this.setTab('images');
      this.activeImageItem = item;
      const navIdx = this.navList.indexOf(item);
      this.activeImageIndex = navIdx >= 0 ? navIdx : 0;

      this.imageItems.forEach((it) => it.classList.remove('is-active'));
      item.classList.add('is-active');

      this.stage.innerHTML = '';
      const img = document.createElement('img');
      img.className = 'mam-stage__img';
      img.src = item.dataset.full;
      img.alt = item.dataset.alt || '';
      this.stage.appendChild(img);
      if (this.stageTitle) {
        const alt = (item.dataset.alt || '').trim();
        this.stageTitle.textContent = alt;
        this.stageTitle.style.display = alt ? '' : 'none';
      }
      item.scrollIntoView({ block: 'nearest' });
    }

    // Compat: selección por índice dentro de la lista navegable.
    selectImage(i) {
      const list = this.navList && this.navList.length ? this.navList : this.imageItems;
      this.selectImageItem(list[i]);
    }

    selectVideo(i) {
      if (!this.videoItems.length) return;
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      this.setTab('videos');
      this.activeVideoIndex = i;

      const item = this.videoItems[i];
      this.videoItems.forEach((it) => it.classList.remove('is-active'));
      item.classList.add('is-active');

      this.stage.innerHTML = '';
      const tpl = item.querySelector('template');
      if (tpl && tpl.content.firstElementChild) {
        const node = tpl.content.firstElementChild.cloneNode(true);
        this.stage.appendChild(node);
        if (node.nodeName === 'VIDEO' && node.getAttribute('autoplay')) {
          // play() devuelve una promesa; si se interrumpe (pause rápido) ignoramos el AbortError
          const p = node.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }
      }
      if (this.stageTitle) {
        const t = (item.dataset.title || '').trim();
        this.stageTitle.textContent = t;
        this.stageTitle.style.display = t ? '' : 'none';
      }
      item.scrollIntoView({ block: 'nearest' });
    }

    step(dir) {
      const list = this.navList && this.navList.length ? this.navList : this.imageItems;
      if (!list.length) return;
      let idx = list.indexOf(this.activeImageItem);
      if (idx < 0) idx = 0;
      const next = (idx + dir + list.length) % list.length;
      this.selectImageItem(list[next]);
    }
  });
}
